import { useEffect, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode, type RefObject } from 'react'
import { ArrowRight, Cpu, ShieldCheck } from 'lucide-react'

import { installPointerLight } from '../../app/ambient/pointerLight'
import { BLOCK_SECONDS } from '../../core/pricing'
import { ExampleBadge } from '../../ui/Badge'
import { LegalFooter } from '../../ui/LegalFooter'
import { LogoMark } from '../../ui/Logo'
import { prefersReducedMotion, useReducedMotion } from '../../ui/motion'
import { PREVIEW_MACHINES, PREVIEW_STEPS } from './preview'
import { acrossScreen, intoView, pastReadingLine, useScrollProgress, writeProgress } from './scroll'
import { WorldStage } from './WorldStage'

/**
 * The public introduction: one service, served from everywhere.
 *
 * The page is a story told by scrolling. A pinned stage (WorldStage) draws the
 * product's idea — a single service, deployed to independent hosts around the
 * world, answering each request from the copy nearest to it, moving when a
 * host fails, paid for block by block — while five short steps scroll past it
 * and say the same thing in words. After the story: what DDWS is in one
 * sentence, the four parts that make it work, how access works, and the
 * workspace itself.
 *
 * Three rules hold everywhere on it, and LandingPage.test enforces each:
 *
 * - One action. Every call to action is the same link to /login, so there is
 *   nothing to choose between and nothing that is a button pretending to be
 *   a link.
 * - No invented data. The stage is an illustration and says so; the preview
 *   rows are marked example; there are no prices, balances or counts. Every
 *   claim in the words is one the tenant guide makes
 *   (docs/content/tenant-guide.md), and where the guide says a thing is not
 *   live yet — auto-recovery has run in the lab, not on a public network — the
 *   step says so too.
 * - The key sentence, once. "DDWS never holds them" appears exactly one time,
 *   where it is first true to say it.
 *
 * The page is a night scene in either theme (`theme-dark` on its root), with
 * one daylight section (`theme-light`) where the parts are laid out, so the
 * tokens rather than one-off colours carry both.
 */
export function LandingPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  useEffect(() => installPointerLight(), [])
  const worldRef = useRef<HTMLElement>(null)
  const barRef = useRef<HTMLElement>(null)
  const previewRef = useRef<HTMLElement>(null)
  useScrollProgress(previewRef, intoView, writeProgress)
  useBarState(barRef)

  const start = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    onNavigate('/login')
  }

  return (
    <div className="landing-page theme-dark">
      <header ref={barRef} className="landing-bar">
        <a className="landing-brand" href="#landing-title">
          <LogoMark className="landing-mark" />
          <span className="landing-brand-copy">
            <strong>DDWS</strong>
            <span>Cloud workspace</span>
          </span>
        </a>
        <nav className="landing-nav" aria-label="On this page">
          <a href="#network">Network</a>
          <a href="#how">How it works</a>
          <a href="#access">Access</a>
        </nav>
        <StartLink onClick={start} className="landing-bar-cta" size="sm" />
      </header>

      <main className="landing-main">
        <section ref={worldRef} className="world" aria-labelledby="landing-title">
          <WorldStage section={worldRef} />
          <div className="world-flow">
            <div className="world-hero">
              <h1 id="landing-title">
                <span className="world-title-line">One service.</span>{' '}
                <span className="world-title-line">Served from everywhere.</span>
              </h1>
              <p className="landing-lede">
                DDWS runs your container on independent hosts around the world: hosts you choose, on a
                public record, paid for by the block.
              </p>
              <div className="world-actions">
                <StartLink onClick={start} size="lg" />
                <a className="world-scroll-cue" href="#network">
                  <span className="world-scroll-mark" aria-hidden="true" />
                  See how it works
                </a>
              </div>
              <p className="landing-trust">
                <ShieldCheck size={16} strokeWidth={1.8} aria-hidden="true" />
                Your keys stay in your wallet. DDWS never holds them.
              </p>
            </div>

            <Step id="network" index={1} side="start" title="A network, not a company.">
              Independent hosts offer their machines on DDWS. Each one is listed on a public chain with its
              audit status and track record, for anyone to check.
            </Step>
            <Step index={2} side="end" title="Describe it once. Run it where you choose.">
              Rent a host you picked at the price it posts, or name the highest price you&rsquo;ll accept and
              choose from the offers hosts make. You sign in your wallet, and your service starts.
            </Step>
            <Step index={3} side="start" title="Close to every user.">
              Hosts list the region they are in. Rent a copy of your service near each of your audiences, so
              their requests have less far to travel.
            </Step>
            <Step index={4} side="end" title="A host fails. Your service moves." status="In the lab">
              Auto-recovery is built for this: a contract on chain holds your service and, when its host
              fails, rents another and starts it there. It has been tested in the lab, not yet on a public
              network.
            </Step>
            <Step index={5} side="start" title="Paid by the block.">
              Rent streams out of a prepaid deposit one block at a time, with a new block about every six
              seconds. Close the rental and what is left returns to your wallet.
            </Step>
          </div>
          <p className="world-note">Illustration, not live network data.</p>
        </section>

        <Statement />

        <div className="landing-marquee" aria-hidden="true">
          <Marquee word="everywhere" />
        </div>

        <section className="landing-parts theme-light" id="how" aria-labelledby="parts-title">
          <div className="landing-section-head">
            <h2 id="parts-title">The parts that make it work.</h2>
            <p>Four ideas carry the whole product. Each one is something you can check for yourself.</p>
          </div>
          <div className="landing-part-grid">
            <Part title="One block at a time" art={<BlockMeter />}>
              Hosts are paid from your deposit block by block, so a rental costs exactly the time it was open.
            </Part>
            <Part title="Checked, not trusted" art={<Fingerprint />}>
              Your rental puts a fingerprint of its configuration on chain, and a host accepts only the
              configuration that matches it.
            </Part>
            <Part title="No network fees" art={<span className="landing-part-zero">0</span>}>
              Transactions on the DDWS network carry no fee. Signing a rental, topping it up and closing it
              cost nothing extra.
            </Part>
            <Part title="Yours to move" art={<Portable />}>
              Your service is a standard container image. Run it on another host, or somewhere else entirely,
              whenever you like.
            </Part>
          </div>
        </section>

        <section className="landing-stages" id="access" aria-labelledby="landing-stages-title">
          <h2 id="landing-stages-title">Explore first. Connect a wallet when it matters.</h2>
          <div className="landing-stage-grid">
            <div className="landing-stage">
              <span className="landing-stage-index" aria-hidden="true">1</span>
              <h3>Your email opens the workspace</h3>
              <p>
                An email address and a password are enough to sign in and look around the console. The examples
                are read-only, so nothing you do here spends anything.
              </p>
            </div>
            <div className="landing-stage">
              <span className="landing-stage-index" aria-hidden="true">2</span>
              <h3>Keplr or Leap activates real usage</h3>
              <p>
                Connecting Keplr or Leap shows your own prepaid balance and instances, and signs a real rental.
                Every transaction is approved inside the extension.
              </p>
            </div>
          </div>
        </section>

        <section className="landing-showcase" aria-labelledby="landing-showcase-title">
          <div className="landing-section-head">
            <h2 id="landing-showcase-title">See the workspace before you rent.</h2>
            <p>This is the screen you land on after signing up, with example machines to explore.</p>
          </div>
          <figure ref={previewRef} className="landing-preview" aria-labelledby="landing-preview-caption">
            <div className="landing-preview-frame">
              <p className="landing-preview-title">Set up your DDWS workspace</p>
              <p className="landing-preview-subtitle">Five simple steps to go from zero to your first instance.</p>
              <ol className="landing-preview-steps">
                {PREVIEW_STEPS.map((step, index) => (
                  <li key={step.title} className={index === 0 ? 'landing-preview-step landing-preview-step-current' : 'landing-preview-step'}>
                    <span className="landing-preview-ordinal" aria-hidden="true">{step.ordinal}</span>
                    <span className="landing-preview-step-copy">
                      <strong>{step.title}</strong>
                      <small>{step.detail}</small>
                    </span>
                  </li>
                ))}
              </ol>
              <p className="landing-preview-section">Explore example machines</p>
              <ul className="landing-preview-rows">
                {PREVIEW_MACHINES.map((machine) => (
                  <li key={machine.id} className="landing-preview-row">
                    <span className="landing-preview-icon" aria-hidden="true">
                      <Cpu size={18} strokeWidth={1.8} />
                    </span>
                    <span className="landing-preview-machine">
                      <span className="landing-preview-machine-name">
                        <strong>{machine.name}</strong>
                        <ExampleBadge />
                      </span>
                      <small>{machine.summary}</small>
                    </span>
                    <dl className="landing-preview-specs">
                      <div><dt>vCPU</dt><dd>{machine.vcpu}</dd></div>
                      <div><dt>Memory</dt><dd>{machine.memory}</dd></div>
                      <div><dt>Storage</dt><dd>{machine.storage}</dd></div>
                      <div><dt>Region</dt><dd>{machine.region}</dd></div>
                    </dl>
                  </li>
                ))}
              </ul>
            </div>
            <figcaption id="landing-preview-caption" className="landing-preview-caption">
              A preview of the DDWS workspace. Every machine shown is a read-only example, not anyone's account.
            </figcaption>
          </figure>
        </section>

        <section className="landing-final" aria-labelledby="landing-final-title">
          <h2 id="landing-final-title">Your service, everywhere.</h2>
          <p>Create a workspace with your email, and connect a wallet only when you are ready to rent.</p>
          <StartLink onClick={start} size="lg" />
        </section>
      </main>

      <footer className="landing-footer">
        <span>Decentralised compute, run by its users</span>
        <LegalFooter />
      </footer>
    </div>
  )
}

/**
 * The page's one action. A link, because it goes somewhere: middle-click and
 * "open in new tab" work, and the in-app navigation is a progressive layer on
 * top of a real href.
 */
function StartLink({ onClick, size, className = '' }: { onClick: (event: MouseEvent<HTMLAnchorElement>) => void; size: 'sm' | 'lg'; className?: string }) {
  return (
    <a href="/login" onClick={onClick} className={`ui-button ui-button-primary ui-button-${size} landing-cta ${className}`.trim()}>
      <span className="ui-button-content">
        Get started
        <ArrowRight size={size === 'lg' ? 18 : 16} strokeWidth={2} aria-hidden="true" />
      </span>
    </a>
  )
}

/** The bar is clear over the opening screen and frosted once the page moves; an attribute, not state. */
function useBarState(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const bar = ref.current
    if (!bar) return undefined
    let frame = 0
    const update = () => {
      frame = 0
      bar.toggleAttribute('data-scrolled', window.scrollY > 8)
    }
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update) }
    update()
    window.addEventListener('scroll', schedule, { passive: true })
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
    }
  }, [ref])
}

/**
 * One step of the story. `status` marks a step whose subject is not live yet;
 * it is a visual echo of what the sentence already says, so it is hidden from
 * assistive technology rather than read twice.
 */
function Step({ id, index, side, title, status, children }: { id?: string; index: number; side: 'start' | 'end'; title: string; status?: string; children: ReactNode }) {
  const headingId = `world-step-${index}`
  return (
    <article id={id} className={`world-step world-step--${side}`} aria-labelledby={headingId}>
      <div className="world-step-card">
        <p className="world-step-index" aria-hidden="true">
          {String(index).padStart(2, '0')} / 05
          {status ? <span className="world-step-status">{status}</span> : null}
        </p>
        <h2 id={headingId}>{title}</h2>
        <p>{children}</p>
      </div>
    </article>
  )
}

const STATEMENT = 'DDWS is the cloud without the landlord. Your container runs on hardware owned by the people who run it, and every rental is a public agreement between you and them that anyone can verify.'

/** The product in one sentence, lit word by word as it passes the reading line. */
function Statement() {
  const ref = useRef<HTMLParagraphElement>(null)
  useScrollProgress(ref, pastReadingLine, writeProgress)
  const words = STATEMENT.split(' ')
  return (
    <section className="landing-statement" aria-labelledby="landing-statement-title">
      <h2 id="landing-statement-title" className="sr-only">What DDWS is</h2>
      <p ref={ref} className="landing-statement-text">
        {words.map((word, index) => (
          <span key={`${word}-${index}`} style={{ '--at': (index / words.length).toFixed(3) } as CSSProperties}>
            {word}
            {index < words.length - 1 ? ' ' : ''}
          </span>
        ))}
      </p>
    </section>
  )
}

/** One very large word, sliding as the page passes it. Decorative: the section is aria-hidden. */
function Marquee({ word }: { word: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useScrollProgress(ref, acrossScreen, writeProgress, { settled: 0.5 })
  return (
    <div ref={ref} className="landing-marquee-track">
      <span>{word}</span>
      <span>{word}</span>
    </div>
  )
}

function Part({ title, art, children }: { title: string; art: ReactNode; children: ReactNode }) {
  return (
    <article className="landing-part">
      <div className="landing-part-art" aria-hidden="true">{art}</div>
      <h3>{title}</h3>
      <p>{children}</p>
    </article>
  )
}

/** Twelve cells; one more lights each block (BLOCK_SECONDS). Still, half full, with reduced motion. */
function BlockMeter() {
  const reduced = useReducedMotion()
  const [tick, setTick] = useState(() => (prefersReducedMotion() ? 6 : Math.floor(Date.now() / (BLOCK_SECONDS * 1000)) % 12))
  useEffect(() => {
    if (reduced) return undefined
    const timer = window.setInterval(() => setTick((current) => (current + 1) % 12), BLOCK_SECONDS * 1000)
    return () => window.clearInterval(timer)
  }, [reduced])
  const shownTick = reduced ? 6 : tick
  return (
    <span className="block-meter">
      {Array.from({ length: 12 }, (_, index) => (
        <span key={index} data-lit={index <= shownTick ? '' : undefined} data-current={index === shownTick ? '' : undefined} />
      ))}
    </span>
  )
}

/** Two fingerprints that match: what you signed, and what runs. */
function Fingerprint() {
  const bars = [6, 11, 4, 9, 13, 7, 10, 5, 12, 8, 6, 11, 9, 4, 13, 7, 10, 12, 5, 8]
  return (
    <span className="fingerprint">
      {[0, 1].map((row) => (
        <span key={row} className="fingerprint-row">
          {bars.map((height, index) => <span key={index} style={{ '--h': height } as CSSProperties} />)}
        </span>
      ))}
      <span className="fingerprint-match">✓</span>
    </span>
  )
}

/** A container stepping from one host to another. */
function Portable() {
  return (
    <span className="portable">
      <span className="portable-host" />
      <span className="portable-host" />
      <span className="portable-box" />
    </span>
  )
}
